import type { ApolloClient } from "@apollo/client";
import { createAsyncThunk, createSelector, createSlice, current, type PayloadAction } from "@reduxjs/toolkit";
import { isArray } from "lodash";
import { shallowEqual } from "react-redux";
import type { RootState } from "store";
import type { LocalDishType, LocalFridgeDishes } from "types/mainTypes";
import { filterAvailableDishTypeIds, filterAvailableDishTypes, sortDishTypes } from "utils/mealListUtils";
import { setShallowEqual } from "utils/setShallowEqual";
import { getAvailableDishTypeIds, mergeDishTypes } from "utils/utils";
import { captureApolloError } from "../services/external/sentry";
import { LightingSegmentModeType, type Price } from "../types";
import { getFridgeId } from "../utils/browser/localStorage";
import { FETCH_DISHTYPE_IDS_AND_PRICES, FETCH_UNKNOWN_DISH_TYPE } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";
import { selectAllergens, selectFilters, selectSelectedLanguage } from "./appStateSlice";
import { selectOrder, selectTransactionDishes } from "./transactionSlice";

type DishesSlice = LocalFridgeDishes;

const initialState: DishesSlice = {
	types: [],
	items: [],
	unknownDishTypeIds: [],
};

type FetchUnknownDishTypesPayload = {
	orderDishTypes: LocalDishType[];
	apolloClient: ApolloClient<unknown>;
};

// Helper function to validate if a dish type has necessary fields for rendering
const isValidDishType = (dishType: LocalDishType): boolean =>
	Boolean(dishType?.id && dishType.description && dishType.price);

export const fetchUnknownDishTypes = createAsyncThunk(
	"dishes/fetchUnknownDishTypesOnTheFly",
	async ({ orderDishTypes, apolloClient }: FetchUnknownDishTypesPayload, { getState, rejectWithValue }) => {
		const state = getState() as RootState;
		const currentDishTypeIds = new Set(state.dishes.types?.map((type) => type.id) || []);
		const fridgeId = getFridgeId();

		const unknownDishTypeIds = orderDishTypes
			.filter((dishType) => dishType?.id && !currentDishTypeIds.has(dishType.id))
			.map((dish) => dish.id)
			.filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates

		if (unknownDishTypeIds.length === 0 || !fridgeId) {
			return [];
		}

		try {
			logToSentry(
				`Fetching unknown dish types on the fly: ${unknownDishTypeIds.join(", ")} for fridge: ${fridgeId}`,
				"info",
			);

			const results = await Promise.all(
				unknownDishTypeIds.map((dishTypeId) =>
					apolloClient
						.query({
							query: FETCH_UNKNOWN_DISH_TYPE,
							variables: { fridgeId: fridgeId, dishTypeId: dishTypeId },
							fetchPolicy: "network-only",
						})
						.catch((error) => {
							captureApolloError(error, {
								kind: "Fetch_Unknown_Dish_Type_Error",
								query: FETCH_UNKNOWN_DISH_TYPE.kind,
								variables: { fridgeId: fridgeId, dishTypeId: dishTypeId },
							});
							return null;
						}),
				),
			);

			const fetchedDishTypes = results
				.filter((result) => result?.data?.fridge?.dishes?.type)
				.map((result) => ({ ...result?.data.fridge.dishes.type }));

			logToSentry(`Successfully fetched ${fetchedDishTypes.length} unknown dish types for fridge: ${fridgeId}`, "info");

			return fetchedDishTypes;
		} catch (error) {
			captureApolloError(error as Error, {
				kind: "Fetch_Unknown_Dish_Types_Batch_Error",
				query: FETCH_UNKNOWN_DISH_TYPE.kind,
				variables: { fridgeId: fridgeId, unknownDishTypeIds: unknownDishTypeIds.join(",") },
			});
			return rejectWithValue((error as Error).message);
		}
	},
);

export const refetchDishTypePrices = createAsyncThunk(
	"dishes/refetchDishTypePrices",
	async (apolloClient: ApolloClient<unknown>, { rejectWithValue }) => {
		const fridgeId = getFridgeId();

		if (!fridgeId) {
			return rejectWithValue("No fridge ID found");
		}

		try {
			const result = await apolloClient.query({
				query: FETCH_DISHTYPE_IDS_AND_PRICES,
				variables: { id: fridgeId },
				fetchPolicy: "network-only",
			});

			return result.data?.fridge?.dishes?.types || [];
		} catch (error) {
			captureApolloError(error as Error, {
				kind: "Refetch_Dish_Type_Prices_Error",
				query: FETCH_DISHTYPE_IDS_AND_PRICES.kind,
				variables: { id: fridgeId },
			});
			return rejectWithValue((error as Error).message);
		}
	},
);

export const dishesSlice = createSlice({
	name: "dishes",
	initialState: initialState,
	reducers: {
		setInitialDishTypes: (state, action) => {
			state.types = action.payload;
		},
		setDishTypes: (state, { payload }: PayloadAction<{ dishTypes: LocalDishType[] | null }>) => {
			const { dishTypes: updatedDishTypes } = payload;

			if (!state.types) {
				if (updatedDishTypes) {
					state.types = updatedDishTypes;
				}
			} else if (isArray(updatedDishTypes)) {
				// A `dishes.types` payload reaching here is an inventory snapshot: merge in updates and any
				// brand-new types (refills add stock), then empty any existing dish type ABSENT from it — the
				// backend drops a dish type once its last dish is taken. Emptying (not deleting) matches prior
				// behaviour; `getAvailableDishTypeIds` then filters the empty type out of the UI. This works the
				// same for orders and refills.
				// IMPORTANT: the BE also pushes SPARSE lighting echoes (a single type, `dishes: null`) that are
				// NOT snapshots — `DeviceContext.onData` routes those to `setDishTypesLighting` via
				// `isLightingEchoFrame` so they never hit this destructive reconcile (it would empty every
				// still-stocked type the echo omits). Do not loosen that gate.
				const merged = mergeDishTypes(current(state.types), updatedDishTypes);
				const payloadDishTypeIds = new Set(updatedDishTypes.map((dishType) => dishType.id));
				state.types = merged.map((dishType) =>
					payloadDishTypeIds.has(dishType.id) || !(dishType.dishes && dishType.dishes.length > 0)
						? dishType
						: { ...dishType, dishes: [] },
				);
			} else if (updatedDishTypes) {
				state.types = [...mergeDishTypes(current(state.types), updatedDishTypes)];
			}
		},
		// Lighting-only echo from the subscription (the BE reflects a master's spotlight TOGGLE as a
		// SPARSE single-type frame: only the changed type, lighting.mode set, `dishes: null`). It must
		// update per-type lighting.mode ONLY — never the type set or any `dishes` array. Routing it here
		// (instead of `setDishTypes`) is what stops the destructive snapshot reconcile from emptying every
		// other still-stocked type just because the echo omits them (Meal List wipe on detail-open).
		setDishTypesLighting: (state, { payload }: PayloadAction<{ dishTypes: LocalDishType[] }>) => {
			if (!state.types) {
				return;
			}
			const modeById = new Map(
				payload.dishTypes
					.filter((dishType) => dishType?.id && dishType.lighting?.mode != null)
					.map((dishType) => [dishType.id, dishType.lighting?.mode] as const),
			);
			if (modeById.size === 0) {
				return;
			}
			state.types = state.types.map((dishType) => {
				const mode = modeById.get(dishType.id);
				return mode != null && dishType.lighting?.mode !== mode
					? { ...dishType, lighting: { ...dishType.lighting, mode: mode } }
					: dishType;
			});
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUnknownDishTypes.fulfilled, (state, action: { payload: LocalDishType[] }) => {
				const fetchedDishTypes = action.payload;

				if (fetchedDishTypes.length > 0) {
					// Filter out incomplete dish types
					// that don't have necessary fields for rendering
					const validDishTypes = fetchedDishTypes.filter(isValidDishType);

					if (validDishTypes.length > 0) {
						// Add only the valid fetched dish types to the existing types
						if (state.types) {
							state.types = [...mergeDishTypes(current(state.types), [...validDishTypes])];
						} else {
							state.types = [...validDishTypes];
						}

						// Clear the unknown dish type IDs
						// that were successfully fetched and validated
						const validFetchedIds = validDishTypes.map((dishType) => dishType.id);
						state.unknownDishTypeIds = state.unknownDishTypeIds.filter((id) => !validFetchedIds.includes(id));

						// Log successful validation
						logToSentry(
							`Added ${validDishTypes.length} valid dish types out of ${fetchedDishTypes.length} fetched`,
							"info",
						);
					}

					// Log if some dish types were filtered out due to incomplete data
					if (validDishTypes.length < fetchedDishTypes.length) {
						const invalidCount = fetchedDishTypes.length - validDishTypes.length;
						logToSentry(
							`Filtered out ${invalidCount} incomplete dish types that lack necessary fields for rendering`,
							"warning",
						);
					}
				}
			})
			.addCase(fetchUnknownDishTypes.rejected, (_state, action) => {
				logToSentry("Failed to fetch unknown dish types on the fly", "error", [
					{ payload: JSON.stringify(action.payload) },
				]);
			})
			.addCase(
				refetchDishTypePrices.fulfilled,
				(state, action: PayloadAction<{ id: string; price: Price }[] | null>) => {
					const updatedPrices = action.payload;
					if (state.types && updatedPrices != null && updatedPrices.length > 0) {
						const priceMap = new Map(updatedPrices.map((priceData) => [priceData.id, priceData.price]));

						state.types.forEach((dishType, index) => {
							const updatedPrice = priceMap.get(dishType.id);
							if (updatedPrice && state.types) {
								state.types[index].price = updatedPrice;
							}
						});
					}
				},
			)
			.addCase(refetchDishTypePrices.rejected, (_state, action) => {
				logToSentry("Failed to refetch dish type prices", "error", [{ payload: JSON.stringify(action.payload) }]);
			});
	},
});

export const { setDishTypes, setDishTypesLighting, setInitialDishTypes } = dishesSlice.actions;

export const selectDishTypes = createSelector([(state: RootState) => state.dishes.types], (dishTypes) => {
	// We want to return only dishes with price and description
	// that secures that we have the most important data for all the displayed dish types
	return dishTypes?.filter((dishType) => isValidDishType(dishType)) || [];
});

export const selectAvailableDishTypesIds = createSelector(
	selectDishTypes,
	selectOrder,
	selectTransactionDishes,
	(fridgeDishTypes, order, orderDishes) => {
		if (!fridgeDishTypes) {
			return new Set<string>();
		}
		return getAvailableDishTypeIds(fridgeDishTypes, orderDishes, order?.state);
	},
	{
		memoizeOptions: {
			resultEqualityCheck: setShallowEqual,
		},
	},
);
export const selectIsFridgeEmpty = createSelector(selectDishTypes, selectOrder, (fridgeDishTypes, order) => {
	const hasDishTypeWithDishes =
		fridgeDishTypes.length > 0 && fridgeDishTypes.some((dishType) => (dishType.dishes?.length ?? 0) > 0);

	return !(order || hasDishTypeWithDishes);
});
export const selectAvailableDishTypes = createSelector(
	selectDishTypes,
	selectAvailableDishTypesIds,
	selectFilters,
	selectSelectedLanguage,
	(fridgeDishTypes, availableDishTypeIds, filters, selectedLanguage) => {
		const filteredDishes = filterAvailableDishTypes(fridgeDishTypes, availableDishTypeIds, filters.allergens, true);
		return sortDishTypes(filteredDishes, selectedLanguage);
	},
);
export const selectDishTypesNotIncludedInFilters = createSelector(
	selectDishTypes,
	selectAvailableDishTypesIds,
	selectFilters,
	selectSelectedLanguage,
	(fridgeDishTypes, availableDishTypeIds, filters, selectedLanguage) => {
		const filteredDishes = filterAvailableDishTypes(fridgeDishTypes, availableDishTypeIds, filters.allergens, false);
		return sortDishTypes(filteredDishes, selectedLanguage);
	},
);

export const selectFilteredDishTypeIds = createSelector(
	[selectDishTypes, selectAvailableDishTypesIds, selectAllergens],
	(fridgeDishTypes, availableDishTypeIds, allergens) =>
		filterAvailableDishTypeIds(fridgeDishTypes, availableDishTypeIds, allergens),
	{
		memoizeOptions: {
			resultEqualityCheck: shallowEqual,
		},
	},
);

export const selectActualSpotlightDishTypeIds = createSelector(
	[selectDishTypes],
	(dishTypes): readonly string[] | null => {
		// Cold-start signal: until at least one dish carries a non-null
		// lighting.mode, the BE hasn't echoed the per-dish lighting state.
		// Return `null` to mark "unknown" so callers (convergence hook) can
		// distinguish from "echoed empty" (`[]`). Covers both empty-dishes
		// and dishes-without-echo cases (`some` on `[]` is `false`).
		const hasAnyEcho = dishTypes.some((dishType) => dishType.lighting?.mode != null);
		if (!hasAnyEcho) {
			return null;
		}
		return dishTypes
			.filter((dishType) => dishType.lighting?.mode === LightingSegmentModeType.Spotlight)
			.map((dishType) => dishType.id);
	},
	{
		memoizeOptions: {
			resultEqualityCheck: shallowEqual,
		},
	},
);

export default dishesSlice.reducer;
